import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { Toolbar } from '../components/toolbar/toolbar';
import { ToolbarTitle } from '../components/toolbar/toolbar-title';
import { TextBoxFilter } from '../components/input/textbox-filter';
import { SamplingJobDialog } from '../dialog/sampling-job-dialog';
import { type CheckboxChangeEvent, type TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { type SamplingJob, SamplingJobStatus, type SamplingJobStatusRef } from '../services/api.types.ts';
import type { KanbanTaskCardProps } from '../components/kanban/kanban-task-card.types.ts';
import { ErrorRender } from '../components/render/error-render.tsx';
import { KanbanBoard } from '../components/kanban/kanban-board.tsx';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { ToolbarSeparator } from '../components/toolbar/toolbar-separator.tsx';
import { getSamplingJobs, getSamplingJobStatuses, updateSamplingJob } from '../services/api.ts';
import { Checkbox } from '../components/input/checkbox.tsx';
import { SamplingStatusTaskCard } from '../content/kanban/sampling-status-task-card.tsx';
import { DndLayout } from '../components/layout/dnd-layout.tsx';
import { KanbanColumn } from '../components/kanban/kanban-column.tsx';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { DatePicker, type DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { getTargetMonth } from '../utils/date.ts';
import { Button } from '@progress/kendo-react-buttons';
import styles from './sampling-board-status-page.module.scss';

const BREADCRUMB_ITEMS = [
  {
    id: 'homepage',
    text: 'Home',
    url: '/',
    icon: <FontAwesomeIcon icon={faHome} size="lg" />,
  },
  { id: 'sampling-board', text: 'Sampling Board' },
  { id: 'sampling-board-status', text: 'Status' },
];

const CURRENT_SEASON_START = getTargetMonth(5);
const DEFAULT_HIDDEN_STATUSES = new Set([SamplingJobStatus.SamplingComplete, SamplingJobStatus.JobComplete]);

export function SamplingBoardStatusPage() {
  const [samplingJobStatuses, setSamplingJobStatuses] = useState<SamplingJobStatusRef[]>([]);
  const [samplingJobs, setSamplingJobs] = useState<SamplingJob[]>([]);
  const [openSamplingJob, setOpenSamplingJob] = useState<SamplingJob | null>(null);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<number>>(DEFAULT_HIDDEN_STATUSES);
  const [inactiveFlag, setInactiveFlag] = useState<boolean>(false);
  const [activeDragContext, setActiveDragContext] = useState<SamplingJob | null>(null);
  const [textFilter, setTextFilter] = useState<string>('');
  const [fromFilter, setFromFilter] = useState<Date | undefined>(CURRENT_SEASON_START);
  const [toFilter, setToFilter] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const statuses = await getSamplingJobStatuses();
        if (isMounted) {
          setSamplingJobStatuses(statuses);
        }
      } catch (error) {
        console.error('Error loading sampling board:', error);
        if (isMounted) {
          setError('Unable to load sampling board');
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      try {
        const jobs = await getSamplingJobs({
          showInactive: inactiveFlag,
          startDate: fromFilter,
          endDate: toFilter,
        });
        if (isMounted) {
          setSamplingJobs(jobs);
        }
      } catch (error) {
        console.error('Error loading sampling board:', error);
        if (isMounted) {
          setError('Unable to load sampling board');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [inactiveFlag, fromFilter, toFilter]);

  const samplingBoardTasks = useMemo(() => {
    const search = textFilter.trim().toLowerCase();

    return samplingJobs.reduce<SamplingJob[]>((acc, job) => {
      const matches =
        job.companyTitle?.toLowerCase().includes(search) ||
        job.clientTitle?.toLowerCase().includes(search) ||
        job.clientRef?.toLowerCase().includes(search) ||
        `ssj${job.id}`.includes(search) ||
        job.orderNumber?.toLowerCase().includes(search);

      if (matches) {
        acc.push(job);
      }

      return acc;
    }, []);
  }, [textFilter, samplingJobs]);

  const samplingBoardColumns = useMemo(() => {
    return samplingJobStatuses.map((status) => ({
      id: status.id,
      title: status.title,
      data: samplingBoardTasks
        .filter((job) => job.statusId === status.id)
        .sort((a, b) => {
          return b.id - a.id;
        }),
    }));
  }, [samplingBoardTasks, samplingJobStatuses]);

  const renderTaskCard = useCallback((props: KanbanTaskCardProps) => {
    return (
      <SamplingStatusTaskCard
        {...props}
        data={props.data as unknown as SamplingJob}
        onOpenDialog={({ job }) => setOpenSamplingJob(job)}
      />
    );
  }, []);

  const renderDndOverlay = useCallback(() => {
    return <SamplingStatusTaskCard data={activeDragContext!} isOverlay={true} />;
  }, [activeDragContext]);

  const handleFlagChange = ({ value }: CheckboxChangeEvent) => {
    setInactiveFlag(value);
  };

  const handleDateFilterChange = ({ target }: DatePickerChangeEvent) => {
    const { name, value } = target;
    if (name === 'fromDate') {
      setFromFilter(value ?? undefined);
    } else {
      setToFilter(value ?? undefined);
    }
  };

  const handleTextFilterChange = ({ value }: TextBoxChangeEvent) => {
    setTextFilter(String(value));
  };

  const handleTextFilterClear = () => {
    setTextFilter('');
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const jobs = await getSamplingJobs({
        showInactive: inactiveFlag,
        startDate: fromFilter,
        endDate: toFilter,
      });
      setSamplingJobs(jobs);
    } catch (error) {
      console.error('Error loading sampling board:', error);
      setError('Unable to load sampling board');
    } finally {
      setLoading(false);
    }
  };

  const handleKanbanColumnToggleView = (columnId: number) => {
    setHiddenColumnIds((prev) => {
      const next = new Set(prev);

      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }

      return next;
    });
  };

  const handleDragStart = (e: DragStartEvent) => {
    setActiveDragContext(e.active.data.current as SamplingJob);
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveDragContext(null);

    const columnId = over?.id;
    if (columnId == null) {
      return;
    }

    const updatedJob = active?.data?.current as SamplingJob;
    if (updatedJob == null) {
      return;
    }

    const prevJob = { ...updatedJob };

    updatedJob.statusId = Number(columnId);

    setSamplingJobs((prev) => prev.map((job) => (job.id === updatedJob.id ? updatedJob : job)));

    try {
      await updateSamplingJob({
        jobId: updatedJob.id,
        plannedDate: updatedJob.plannedDate!,
        estimatedDays: updatedJob.estimatedDays,
        statusId: updatedJob.statusId,
        active: updatedJob.active,
      });
    } catch (error) {
      console.error('Failed to update kanban board column:', error);
      setSamplingJobs((prev) => prev.map((job) => (job.id === prevJob.id ? prevJob : job)));
    }
  };

  const handleDragCancel = () => {
    setActiveDragContext(null);
  };

  const handleDialogApply = async () => {
    try {
      const jobs = await getSamplingJobs({
        showInactive: inactiveFlag,
        startDate: fromFilter,
        endDate: toFilter,
      });
      setSamplingJobs(jobs);
    } catch (error) {
      console.error('Error loading sampling board:', error);
      setError('Unable to load sampling board');
    } finally {
      handleDialogClose();
    }
  };

  const handleDialogClose = () => {
    setOpenSamplingJob(null);
  };

  return (
    <>
      <DndLayout
        breadcrumb={BREADCRUMB_ITEMS}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        renderOverlay={renderDndOverlay}
      >
        <Toolbar>
          <ToolbarTitle flex={1}>Sampling Status Board</ToolbarTitle>
          <ToolbarSeparator />
          <div style={{ width: 200, marginRight: 5 }}>
            <DatePicker
              name={'fromDate'}
              value={fromFilter}
              max={toFilter}
              format={'EEE, d MMM yy'}
              placeholder={'Filter jobs planned after..'}
              onChange={handleDateFilterChange}
            />
          </div>
          <div style={{ width: 200 }}>
            <DatePicker
              name={'toDate'}
              value={toFilter}
              min={fromFilter}
              format={'EEE, d MMM yy'}
              placeholder={'Filter jobs planned before..'}
              onChange={handleDateFilterChange}
            />
          </div>
          <ToolbarSeparator />
          <TextBoxFilter
            value={textFilter}
            onChange={handleTextFilterChange}
            onClear={handleTextFilterClear}
            style={{ width: 280 }}
            placeholder={'Filter by company, client, job, order no..'}
          />
          <ToolbarSeparator />
          <Checkbox checked={inactiveFlag} label={'Show Inactive'} onChange={handleFlagChange} />
          <ToolbarSeparator />
          <Button themeColor={'primary'} className={styles.refreshBtn} onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} size={'1x'} />
          </Button>
        </Toolbar>
        {loading ? (
          <LoaderRender message={'Loading...'} />
        ) : error.length > 0 ? (
          <ErrorRender message={error} />
        ) : (
          <KanbanBoard className={styles.kanbanBoard}>
            {samplingBoardColumns.map((column) => (
              <KanbanColumn
                {...column}
                key={column.id}
                data={column.data}
                collapsed={hiddenColumnIds?.has(column.id)}
                renderCard={renderTaskCard}
                onToggleView={handleKanbanColumnToggleView}
              />
            ))}
          </KanbanBoard>
        )}
      </DndLayout>
      {openSamplingJob !== null && (
        <SamplingJobDialog
          job={openSamplingJob}
          jobStatuses={samplingJobStatuses}
          onApply={handleDialogApply}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
}
